import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import apartmentService from "../../services/apartment-service";
import IApartment from "../../types/apartment-type";
import { IHouseSwap } from "../../types/houseswap-type";
import { AccommodationType } from "../../utils/accommodationTypes";

export const useApartmentFunctions = () => {
  const [apartments, setApartments] = useState<IApartment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [longTerm, setLongTerm] = useState<IApartment[]>([]);
  const [shortStays, setShortStays] = useState<IApartment[]>([]);
  const [houseSwaps, setHouseSwaps] = useState<IHouseSwap[]>([]);

  const intl = useIntl();

  const loadApartments = async () => {
    setIsLoading(true);
    try {
      const response = await apartmentService.getAllApartments();
      const apartments = response.data;
      setApartments(apartments);
      setLongTerm(
        apartments.filter(
          (apartment: IApartment) => apartment.listingType === "long-term"
        )
      );
      setShortStays(
        apartments.filter(
          (apartment: IApartment) => apartment.listingType === "short-stays"
        )
      );
      setHouseSwaps(
        apartments.filter(
          (apartment: IHouseSwap) => apartment.listingType === "house-swaps"
        )
      );
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching apartments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const convertSize = (
    size: string,
    fromUnit: "m²" | "sqft",
    toUnit: "m²" | "sqft"
  ): string => {
    if (fromUnit === toUnit) {
      return size;
    }
    if (fromUnit === "m²" && toUnit === "sqft") {
      return (parseFloat(size) * 10.7639).toFixed(0).toString(); // Convert m² to sqft
    } else if (fromUnit === "sqft" && toUnit === "m²") {
      return (parseFloat(size) * 0.092903).toFixed(0).toString(); // Convert sqft to m²
    }
    return size;
  };

  const TranslatedApartmentOptions = (
    accommodationTypes: AccommodationType[]
  ) => {
    const translatedAccommodations = accommodationTypes.map((type) => ({
      ...type,
      translatedLabel: intl.formatMessage({ id: type.labelId }),
    }));

    const sortedAccommodations = translatedAccommodations.sort((a, b) =>
      a.translatedLabel.localeCompare(b.translatedLabel)
    );

    return useMemo(
      () =>
        sortedAccommodations.map((option) => ({
          ...option,
          label: intl.formatMessage({
            id: option.labelId,
            defaultMessage: option.labelId,
          }),
        })),
      [sortedAccommodations]
    );
  };

  const FilterAccommodationCards = (
    accommodationTypes: AccommodationType[],
    listingType: string,
    selectedGroups: string[]
  ) => {
    // Filter by listingType first
    const filteredAccommodationTypes = accommodationTypes.filter(
      (type) => type.group === listingType
    );

    // Further filter by selectedGroups
    const filteredByGroups = selectedGroups.includes("all")
      ? filteredAccommodationTypes
      : filteredAccommodationTypes.filter((type) =>
          selectedGroups.includes(type.group)
        );

    const translatedAccommodations = filteredByGroups.map((type) => ({
      ...type,
      translatedLabel: intl.formatMessage({ id: type.labelId }),
    }));

    return useMemo(
      () =>
        translatedAccommodations.map((option) => ({
          ...option,
          label: intl.formatMessage({
            id: option.labelId,
            defaultMessage: option.labelId,
          }),
        })),
      [translatedAccommodations]
    );
  };

  useEffect(() => {
    loadApartments();
  }, []);

  return {
    apartments,
    isLoading,
    longTerm,
    shortStays,
    houseSwaps,
    convertSize,
    setApartments,
    loadApartments,
    FilterAccommodationCards,
    TranslatedApartmentOptions,
  };
};
